/**
 * Classroom report (per-student Holland planet columns R–C).
 *
 * Recent change (CRM-4204): Planet completion columns match UserService / UserProgression rules
 * instead of only comparing current planet order. A planet counts complete when the student has
 * moved past that planet, or is still on that planet with checkpoint >= Leaving (fixes cases such
 * as finished planet C at checkpoint 12 / BetweenPlanets where the old UI required checkpoint > 12).
 * Shared logic: isHollandPlanetComplete().
 */
import * as React from 'react';
import { RouteComponentProps, withRouter, Link, NavLink, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import bindAllOfThis from 'src/utils/BindThisHelper';
import { PageProps } from "src/models/common/ComponentProps";
import { Row, Col, Icon, Spin, Table, Input, Button } from "antd";
import ReportApiController from "src/api/ReportApiController";
import ClassroomReportResponseDTO from "src/models/report/ClassroomReportResponseDTO";
import { Planets, Planet } from "src/constants/Planets";
import { Grades } from "src/constants/Grades";
import { Checkpoints, Checkpoint } from "src/constants/Checkpoints";
import { ReduxStoreModel } from "src/redux/ReduxModel";
import { UserState } from "src/redux/UserReducer";
import { withLocalize, LocalizeContextProps, Translate } from "react-localize-redux";
import { NameOf } from "src/utils/NameOf";
import ClassroomReportStudentsDTO from "src/models/report/ClassroomReportStudentsDTO";

import DataTable, { DataTableButton } from 'src/components/common/DataTable';

/**
 * Matches UserService progression completion for Holland planets: target planet is "done" when
 * the student has moved past it, or is still on that planet with checkpoint >= Leaving.
 */
function isHollandPlanetComplete(currentPlanet: Planet, checkpoint: Checkpoint, targetPlanet: Planet): boolean {
    return (
        currentPlanet.order > targetPlanet.order ||
        (currentPlanet.id === targetPlanet.id && checkpoint.order >= Checkpoints.Leaving.order)
    );
}

interface ClassroomReportRow {
    firstname: string;
    lastname: string;
    fullname: string;
    level: string;
    R: boolean;
    I: boolean;
    A: boolean;
    S: boolean;
    E: boolean;
    C: boolean;
}

interface RouteParams {
    classroomId: string;
}

interface ClassroomReportProps extends PageProps<RouteParams>, LocalizeContextProps {
    // Redux
    User: UserState;
}

interface ClassroomReportState {
    classroomId: number;
    classroomName: string;
    students: ClassroomReportRow[];
    isLoading: boolean;
}

class ClassroomReport extends React.Component<ClassroomReportProps, ClassroomReportState> {
    constructor(props: ClassroomReportProps) {
        super(props);
        bindAllOfThis(this, ClassroomReport.prototype);

        let classroomId = parseInt(this.props.match.params.classroomId, 10);

        this.state = {
            classroomId: classroomId,
            classroomName: "",
            students: [],
            isLoading: true,
        };
    }

    componentDidMount(): void {
        this.deriveStateFromProps(this.props);
    }

    componentWillReceiveProps(nextProps: ClassroomReportProps) {
        if (this.props.activeLanguage !== nextProps.activeLanguage) {
            // Update the translation array
            this.deriveStateFromProps(nextProps);
        }
    }

    deriveStateFromProps(props: ClassroomReportProps) {
        let classroomId = parseInt(this.props.match.params.classroomId, 10);
        ReportApiController.getClassroomReport({ classroomId: classroomId }).then(response => {
            if (response.ok) {
                // Doing a thing
                let students = response.data.students.map<ClassroomReportRow>(student => {
                    let planet = Planets.FindById(student.planetId);
                    let checkpoint = Checkpoints.FindById(student.checkpointId);

                    return {
                        firstname: student.firstName,
                        lastname: student.lastName,
                        fullname: `${student.lastName}, ${student.firstName}`,
                        level: student.grade,
                        R: isHollandPlanetComplete(planet, checkpoint, Planets.R),
                        I: isHollandPlanetComplete(planet, checkpoint, Planets.I),
                        A: isHollandPlanetComplete(planet, checkpoint, Planets.A),
                        S: isHollandPlanetComplete(planet, checkpoint, Planets.S),
                        E: isHollandPlanetComplete(planet, checkpoint, Planets.E),
                        C: isHollandPlanetComplete(planet, checkpoint, Planets.C),
                    };
                });

                this.setState({
                    classroomId: response.data.classroomId,
                    classroomName: response.data.classroomName,
                    students: students,
                    isLoading: false,
                });
            }
        });
    }

    render() {
        if (this.state.isLoading) {
            return <div className="teacher-reports">
                <h2><Translate id="report.classroom.headerText" /></h2>
                <p><Translate id="report.classroom.loadingText" /></p>
                <Spin />
            </div>;
        }
        return <div className="teacher-reports">
        <h2><Translate id="report.classroom.headerText" /></h2>
            <h3>{this.state.classroomName}</h3>
            <DataTable
                data={this.state.students}
                className="report-container-new"
                tableProps={{ rowKey: 'activityId', loading: this.state.isLoading }}
                columns={[
                    DataTable.StandardColumns.Text("report.classroom.header.fullname", "fullname"),
                    DataTable.StandardColumns.Text("report.classroom.header.level", "level"),
                    {
                        renderer: false,
                        columnProps: {
                            key: "R",
                            align: "center",
                            title: "R",
                            render: (value, row, index) => row.R === true ? <Icon type="check" /> : null
                        }
                    }, {
                        renderer: false,
                        columnProps: {
                            key: "I",
                            align: "center",
                            title: "I",
                            render: (value, row, index) => row.I === true ? <Icon type="check" /> : null
                        }
                    }, {
                        renderer: false,
                        columnProps: {
                            key: "A",
                            align: "center",
                            title: "A",
                            render: (value, row, index) => row.A === true ? <Icon type="check" /> : null
                        }
                    }, {
                        renderer: false,
                        columnProps: {
                            key: "S",
                            align: "center",
                            title: "S",
                            render: (value, row, index) => row.S === true ? <Icon type="check" /> : null
                        }
                    }, {
                        renderer: false,
                        columnProps: {
                            key: "E",
                            align: "center",
                            title: "E",
                            render: (value, row, index) => row.E === true ? <Icon type="check" /> : null
                        }
                    }, {
                        renderer: false,
                        columnProps: {
                            key: "C",
                            align: "center",
                            title: "C",
                            render: (value, row, index) => row.C === true ? <Icon type="check" /> : null
                        }
                    }
                ]}
                // buttonBar={[
                //     new DataTableButton("dataTable.export", () => window.location.href = "/" + ReportApiController.RouteExportClassroomReport(this.state.classroomId))
                // ]}
            />
        </div>;
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User
    };
}

export default connect(mapStateToProps)(withLocalize(ClassroomReport));



// WEBPACK FOOTER //
// ./src/pages/admin/ClassroomReport.tsx